<template>
  <div :class="cardClasses">
    <div v-if="title || subtitle || $slots.header" class="card__header">
      <slot name="header">
        <div class="card__header-content">
          <h3 v-if="title" class="card__title">{{ title }}</h3>
          <p v-if="subtitle" class="card__subtitle">{{ subtitle }}</p>
        </div>
        <div v-if="$slots['header-actions']" class="card__header-actions">
          <slot name="header-actions" />
        </div>
      </slot>
    </div>
    <div v-if="showDivider && (title || subtitle || $slots.header)" class="card__divider"></div>
    <div :class="['card__body', { 'card__body--no-padding': noPadding }]">
      <slot />
    </div>
    <div v-if="showDivider && $slots.footer" class="card__divider"></div>
    <div v-if="$slots.footer" class="card__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseCard',
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'elevated',
      validator: (v) => ['elevated', 'outlined', 'flat'].includes(v)
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    showDivider: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cardClasses() {
      return [
        'card',
        `card--${this.variant}`,
        {
          'card--hoverable': this.hoverable,
          'card--clickable': this.clickable
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.card {
  background-color: $color-bg-primary;
  border-radius: $card-border-radius;
  transition: all $transition-normal $transition-timing;

  &--elevated {
    box-shadow: $shadow-default;
    border: 1px solid $color-border-light;
  }

  &--outlined {
    box-shadow: none;
    border: 1px solid $color-border-default;
  }

  &--flat {
    box-shadow: none;
    border: none;
    background-color: $color-gray-50;
  }

  &--hoverable {
    &:hover {
      transform: translateY(-2px);
      box-shadow: $shadow-lg;
    }
  }

  &--clickable {
    cursor: pointer;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: $spacing-4 $spacing-5;
  }

  &__header-content {
    flex: 1;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: $spacing-2;
    margin-left: $spacing-4;
  }

  &__title {
    font-size: $font-size-lg;
    font-weight: $font-weight-semibold;
    color: $color-text-primary;
    margin: 0;
    line-height: $line-height-tight;
  }

  &__subtitle {
    font-size: $font-size-sm;
    color: $color-text-secondary;
    margin: $spacing-1 0 0;
  }

  &__divider {
    height: 1px;
    background-color: $color-border-light;
  }

  &__body {
    padding: $spacing-4 $spacing-5;

    &--no-padding {
      padding: 0;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $spacing-3;
    padding: $spacing-3 $spacing-5;
  }
}
</style>
